import { Link } from '@tanstack/react-router';
import { FileQuestion, TriangleAlert } from 'lucide-react';

import { getErrorMessage } from '@/shared/api/http';

import { Button, Card, CardContent } from './ui';

import type { ReactNode } from 'react';

function FallbackShell({
  icon,
  title,
  message,
  actions,
}: {
  icon: ReactNode;
  title: string;
  message: string;
  actions: ReactNode;
}) {
  return (
    <div dir="ltr" className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-surface-muted text-foreground-muted [&_svg]:size-6">
            {icon}
          </span>
          <div className="flex flex-col gap-1.5">
            <h1 className="text-lg font-semibold text-foreground">{title}</h1>
            <p className="text-sm text-foreground-muted">{message}</p>
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </CardContent>
      </Card>
    </div>
  );
}

const homeLinkClass =
  'inline-flex h-10 items-center rounded-md bg-brand px-4 text-sm font-medium text-brand-foreground transition-colors hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/** Root notFoundComponent — styled 404 instead of a blank screen. */
export function NotFoundState() {
  return (
    <FallbackShell
      icon={<FileQuestion />}
      title="Page not found"
      message="The page you’re looking for doesn’t exist or has moved."
      actions={
        <Link to="/" className={homeLinkClass}>
          Back to home
        </Link>
      }
    />
  );
}

/** Root errorComponent — styled fallback for route/loader errors. */
export function RouteErrorState({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <FallbackShell
      icon={<TriangleAlert />}
      title="Something went wrong"
      message={getErrorMessage(error)}
      actions={
        <>
          <Button variant="secondary" onClick={reset}>
            Try again
          </Button>
          <Link to="/" className={homeLinkClass}>
            Back to home
          </Link>
        </>
      }
    />
  );
}
